import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, API, apiFetch } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { ArrowLeft, Lock, User, Mail, Zap, Loader2, Save, Camera, Trash2, X, AlertTriangle, UserX } from 'lucide-react';
import Cropper from 'react-easy-crop';
import MainNav from '../components/MainNav';

// Helper function to create cropped image
const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.setAttribute('crossOrigin', 'anonymous');
    image.src = url;
  });

const getCroppedImg = async (imageSrc, pixelCrop) => {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height
  );

  return canvas.toDataURL('image/png').split(',')[1]; // Return base64 without prefix
};

export default function Settings() {
  const { user, refreshUser, logout } = useAuth();
  const navigate = useNavigate();
  
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  
  // Avatar state
  const [showAvatarModal, setShowAvatarModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  
  // Delete avatar confirmation state
  const [showRemoveAvatarDialog, setShowRemoveAvatarDialog] = useState(false);
  
  // Delete account state
  const [showDeleteAccountDialog, setShowDeleteAccountDialog] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (!newPassword) {
      toast.error('Please enter a new password');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setIsChangingPassword(true);
    try {
      const res = await fetch(`${API}/auth/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          new_password: newPassword
        })
      });
      
      if (res.ok) {
        toast.success('Password changed successfully!');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to change password');
      }
    } catch (error) {
      toast.error('Failed to change password');
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image must be less than 5MB');
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        setSelectedImage(reader.result);
        setShowAvatarModal(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleSaveAvatar = async () => {
    if (!selectedImage || !croppedAreaPixels) return;
    
    setIsUploadingAvatar(true);
    try {
      const croppedImageBase64 = await getCroppedImg(selectedImage, croppedAreaPixels);
      
      const res = await fetch(`${API}/auth/update-avatar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ avatar_base64: croppedImageBase64 })
      });
      
      if (res.ok) {
        await refreshUser();
        toast.success('Avatar updated!');
        setShowAvatarModal(false);
        setSelectedImage(null);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to update avatar');
      }
    } catch (error) {
      toast.error('Failed to update avatar');
    } finally {
      setIsUploadingAvatar(false);
    }
  };

  const handleConfirmRemoveAvatar = async () => {
    setShowRemoveAvatarDialog(false);
    try {
      const res = await fetch(`${API}/auth/remove-avatar`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (res.ok) {
        await refreshUser();
        toast.success('Avatar removed permanently');
      }
    } catch (error) {
      toast.error('Failed to remove avatar');
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') {
      toast.error('Please type DELETE to confirm');
      return;
    }
    
    setIsDeletingAccount(true);
    try {
      await apiFetch('/auth/delete-account', {
        method: 'DELETE'
      });
      
      toast.success('Your account has been deleted');
      setShowDeleteAccountDialog(false);
      
      // Clear user state and redirect to home
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Delete account error:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete account');
    } finally {
      setIsDeletingAccount(false);
    }
  };

  return (
    <div className="min-h-screen bg-dark">
      {/* Navigation */}
      <MainNav />

      {/* Main Content */}
      <main className="pt-24 pb-12 container-narrow">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Account Settings</h1>
          <p className="text-zinc-400">Manage your account and security</p>
        </div>

        {/* Avatar Section */}
        <div className="glass-card p-6 mb-6">
          <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
            <Camera className="h-5 w-5 text-primary" />
            Profile Picture
          </h2>
          
          <div className="flex items-center gap-6">
            {/* Current Avatar */}
            <div className="relative">
              <div className="w-24 h-24 rounded-full overflow-hidden bg-primary/20 flex items-center justify-center">
                {user?.picture ? (
                  <img src={user.picture} alt={user.name} className="w-full h-full object-cover" />
                ) : (
                  <User className="h-10 w-10 text-primary" />
                )}
              </div>
            </div>
            
            {/* Upload Controls */}
            <div className="space-y-3">
              <div className="flex gap-2">
                <label className="btn-primary px-4 py-2 rounded-lg cursor-pointer flex items-center gap-2 text-sm">
                  <Camera className="h-4 w-4" />
                  Upload Photo
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileSelect}
                    className="hidden"
                    data-testid="avatar-input"
                  />
                </label>
                {user?.picture && (
                  <Button
                    variant="outline"
                    onClick={() => setShowRemoveAvatarDialog(true)}
                    className="bg-red-500/10 border-red-500/20 text-red-400 hover:bg-red-500/20"
                    data-testid="remove-avatar-btn"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Remove
                  </Button>
                )}
              </div>
              <p className="text-sm text-zinc-400">PNG, JPG up to 5MB. Square images work best.</p>
            </div>
          </div>
        </div>

        {/* Profile Info */}
        <div className="glass-card p-6 mb-6">
          <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
            <User className="h-5 w-5 text-primary" />
            Profile Information
          </h2>
          
          <div className="space-y-4">
            <div>
              <Label className="text-zinc-400 text-sm">Name</Label>
              <p className="text-white text-lg">{user?.name || 'N/A'}</p>
            </div>
            <div>
              <Label className="text-zinc-400 text-sm">Email</Label>
              <p className="text-white text-lg flex items-center gap-2">
                <Mail className="h-4 w-4 text-zinc-400" />
                {user?.email || 'N/A'}
              </p>
            </div>
            <div>
              <Label className="text-zinc-400 text-sm">Credits</Label>
              <p className="text-white text-lg flex items-center gap-2">
                <Zap className="h-4 w-4 text-primary" />
                {user?.credits || 0} credits
              </p>
            </div>
          </div>
        </div>

        {/* Change Password */}
        <div className="glass-card p-6">
          <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
            <Lock className="h-5 w-5 text-primary" />
            Change Password
          </h2>
          
          <form onSubmit={handleChangePassword} className="space-y-4 max-w-md">
            <div>
              <Label htmlFor="newPassword" className="text-white mb-2 block">New Password</Label>
              <Input
                id="newPassword"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="At least 6 characters"
                className="input-dark"
                data-testid="new-password-input"
              />
            </div>
            
            <div>
              <Label htmlFor="confirmNewPassword" className="text-white mb-2 block">Confirm New Password</Label>
              <Input
                id="confirmNewPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                className="input-dark"
                data-testid="confirm-new-password-input"
              />
            </div>
            
            <Button 
              type="submit" 
              disabled={isChangingPassword}
              className="btn-primary"
              data-testid="change-password-btn"
            >
              {isChangingPassword ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Change Password
            </Button>
          </form>
        </div>

        {/* Danger Zone - Delete Account */}
        <div className="glass-card p-6 border border-red-500/20 bg-red-500/5">
          <h2 className="text-xl font-semibold text-red-400 mb-4 flex items-center gap-2">
            <UserX className="h-5 w-5" />
            Danger Zone
          </h2>
          
          <div className="space-y-4">
            <div>
              <p className="text-zinc-300 mb-2">
                Once you delete your account, there is no going back. Please be certain.
              </p>
              <p className="text-zinc-400 text-sm">
                This will permanently delete your account and remove all your data including songs, album covers, gift cards, and personal information.
              </p>
            </div>
            
            <Button
              variant="outline"
              onClick={() => setShowDeleteAccountDialog(true)}
              className="bg-red-500/10 border-red-500/30 text-red-400 hover:bg-red-500/20 hover:border-red-500/50"
              data-testid="delete-account-btn"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete My Account
            </Button>
          </div>
        </div>
      </main>

      {/* Avatar Crop Modal */}
      <Dialog open={showAvatarModal} onOpenChange={setShowAvatarModal}>
        <DialogContent className="bg-zinc-900 border-white/10 text-white sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white">Crop Your Photo</DialogTitle>
          </DialogHeader>
          
          <div className="relative h-80 bg-black rounded-lg overflow-hidden">
            {selectedImage && (
              <Cropper
                image={selectedImage}
                crop={crop}
                zoom={zoom}
                aspect={1}
                cropShape="round"
                showGrid={false}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            )}
          </div>
          
          {/* Zoom Slider */}
          <div className="space-y-2">
            <Label className="text-zinc-300">Zoom</Label>
            <input
              type="range"
              min={1}
              max={3}
              step={0.1}
              value={zoom}
              onChange={(e) => setZoom(parseFloat(e.target.value))}
              className="w-full accent-primary"
            />
          </div>
          
          <div className="flex gap-3 mt-4">
            <Button
              variant="outline"
              onClick={() => {
                setShowAvatarModal(false);
                setSelectedImage(null);
              }}
              className="flex-1 bg-white/5 border-white/10 text-white hover:bg-white/10"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveAvatar}
              disabled={isUploadingAvatar}
              className="flex-1 btn-primary"
              data-testid="save-avatar-btn"
            >
              {isUploadingAvatar ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Save Photo
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Remove Avatar Confirmation Dialog */}
      <AlertDialog open={showRemoveAvatarDialog} onOpenChange={setShowRemoveAvatarDialog}>
        <AlertDialogContent className="bg-zinc-900 border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              Remove Avatar?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              <span className="block mb-2">
                Are you sure you want to remove your profile picture?
              </span>
              <span className="block text-red-400 font-medium">
                This action is permanent and cannot be undone.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-white/5 border-white/10 text-white hover:bg-white/10">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleConfirmRemoveAvatar}
              className="bg-red-600 text-white hover:bg-red-700"
              data-testid="confirm-remove-avatar-btn"
            >
              Remove Permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Account Confirmation Dialog */}
      <AlertDialog open={showDeleteAccountDialog} onOpenChange={(open) => {
        setShowDeleteAccountDialog(open);
        if (!open) setDeleteConfirmText('');
      }}>
        <AlertDialogContent className="bg-zinc-900 border-red-500/30">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-400 flex items-center gap-2">
              <AlertTriangle className="h-6 w-6" />
              Delete Your Account?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-300 space-y-4">
              <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 mt-2">
                <p className="text-red-400 font-semibold mb-2">
                  Warning: This action cannot be undone!
                </p>
                <p className="text-zinc-400 text-sm">
                  The following will be permanently deleted:
                </p>
                <ul className="text-zinc-400 text-sm mt-2 space-y-1 list-disc list-inside">
                  <li>All your songs</li>
                  <li>All your album covers</li>
                  <li>All your gift cards</li>
                  <li>Your personal information and account</li>
                  <li>Any remaining credits</li>
                </ul>
              </div>
              
              <div className="pt-2">
                <Label className="text-zinc-300 text-sm mb-2 block">
                  Type <span className="font-mono text-red-400 font-bold">DELETE</span> to confirm:
                </Label>
                <Input
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder="Type DELETE"
                  className="input-dark border-red-500/30 focus:border-red-500"
                  data-testid="delete-confirm-input"
                />
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel 
              className="bg-white/5 border-white/10 text-white hover:bg-white/10"
              disabled={isDeletingAccount}
            >
              Cancel
            </AlertDialogCancel>
            <Button
              onClick={handleDeleteAccount}
              disabled={deleteConfirmText !== 'DELETE' || isDeletingAccount}
              className="bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
              data-testid="confirm-delete-account-btn"
            >
              {isDeletingAccount ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Forever
                </>
              )}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
